import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren
} from "react";

import { API_BASE } from "../config";
import {
  clearAuth,
  restoreAuthFromStorage,
  setAuthFromLogin
} from "../api/Client";
import { SecureStorage } from "../storage/SecureStorage";
import {
  canAccessPath,
  getDefaultAuthorizedRoute
} from "./permissions";

export type ProfessionalSessionUser = {
  id: number;
  tenantId: number | null;
  tenantName: string | null;
  tenantSlug: string | null;
  fullName: string;
  email: string;
  phone: string;
  sex: string;
  dateOfBirth: string;
  postcode?: string | null;
  postAddress?: string | null;
  dni?: string | null;
  profilePictureName?: string | null;
  permissions: string[];
};

export type ProfessionalTenantOption = {
  membershipKey: string;
  coachId: number;
  tenantId: number;
  tenantName: string;
  tenantSlug: string | null;
  coachName: string;
};

export type ProfessionalLoginResult =
  | {
      status: "authenticated";
      user: ProfessionalSessionUser;
    }
  | {
      status: "select_tenant";
      selectionToken: string;
      options: ProfessionalTenantOption[];
    };

type ProfessionalAuthContextValue = {
  ready: boolean;
  user: ProfessionalSessionUser | null;
  permissions: string[];
  isAuthenticated: boolean;
  defaultRoute: string;
  login: (email: string, password: string) => Promise<ProfessionalLoginResult>;
  selectTenant: (selectionToken: string, membershipKey: string) => Promise<ProfessionalSessionUser>;
  logout: () => Promise<void>;
  canAccess: (pathname: string) => boolean;
};

const ProfessionalAuthContext = createContext<ProfessionalAuthContextValue | null>(null);

function getErrorMessage(payload: unknown, fallback: string) {
  if (!payload || typeof payload !== "object") {
    return fallback;
  }

  const maybeError = "error" in payload ? payload.error : null;
  const maybeMessage = "message" in payload ? payload.message : null;

  if (typeof maybeError === "string" && maybeError.trim()) {
    return maybeError;
  }
  if (typeof maybeMessage === "string" && maybeMessage.trim()) {
    return maybeMessage;
  }
  return fallback;
}

async function parseJsonSafe(response: Response) {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

async function refreshProfessionalSession(refreshToken: string) {
  const response = await fetch(`${API_BASE}/api/professionals/auth/refresh`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${refreshToken}`
    }
  });

  const payload = await parseJsonSafe(response);
  if (!response.ok || !payload?.accessToken || !payload?.refreshToken || !payload?.user) {
    return null;
  }

  return payload as {
    accessToken: string;
    refreshToken: string;
    user: ProfessionalSessionUser;
  };
}

export function ProfessionalAuthProvider({ children }: PropsWithChildren) {
  const [ready, setReady] = useState(false);
  const [user, setUser] = useState<ProfessionalSessionUser | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function restoreSession() {
      try {
        const { access, refresh } = await restoreAuthFromStorage();

        if (!access && !refresh) {
          if (!cancelled) {
            setUser(null);
            setReady(true);
          }
          return;
        }

        if (refresh) {
          const refreshedSession = await refreshProfessionalSession(refresh);
          if (refreshedSession?.user) {
            await setAuthFromLogin(refreshedSession);
            if (!cancelled) {
              setUser(refreshedSession.user);
              setReady(true);
            }
            return;
          }
        }

        if (access) {
          const response = await fetch(`${API_BASE}/api/professionals/auth/session`, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${access}`
            }
          });

          const payload = await parseJsonSafe(response);
          if (response.ok && payload?.user) {
            await SecureStorage.saveUser(payload.user);
            if (!cancelled) {
              setUser(payload.user as ProfessionalSessionUser);
              setReady(true);
            }
            return;
          }
        }

        await clearAuth();
        if (!cancelled) {
          setUser(null);
          setReady(true);
        }
      } catch {
        if (!cancelled) {
          setUser(null);
          setReady(true);
        }
      }

      if (!cancelled) {
        setReady(true);
      }
    }

    void restoreSession();

    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<ProfessionalLoginResult> => {
    const response = await fetch(`${API_BASE}/api/professionals/auth/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        email: String(email || "").trim(),
        password
      })
    });

    const payload = await parseJsonSafe(response);
    if (!response.ok) {
      throw new Error(getErrorMessage(payload, "No se pudo iniciar sesión"));
    }

    if (payload?.status === "select_tenant") {
      return {
        status: "select_tenant",
        selectionToken: String(payload.selectionToken || ""),
        options: Array.isArray(payload.options) ? payload.options : []
      };
    }

    if (!payload?.accessToken || !payload?.refreshToken || !payload?.user) {
      throw new Error("La respuesta del login es inválida");
    }

    await setAuthFromLogin(payload);
    setUser(payload.user as ProfessionalSessionUser);

    return {
      status: "authenticated",
      user: payload.user as ProfessionalSessionUser
    };
  }, []);

  const selectTenant = useCallback(
    async (selectionToken: string, membershipKey: string) => {
      const response = await fetch(`${API_BASE}/api/professionals/auth/select-tenant`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          selectionToken,
          membershipKey
        })
      });

      const payload = await parseJsonSafe(response);
      if (!response.ok) {
        throw new Error(getErrorMessage(payload, "No se pudo completar el acceso al negocio"));
      }

      if (!payload?.accessToken || !payload?.refreshToken || !payload?.user) {
        throw new Error("La respuesta de selección de negocio es inválida");
      }

      await setAuthFromLogin(payload);
      setUser(payload.user as ProfessionalSessionUser);
      return payload.user as ProfessionalSessionUser;
    },
    []
  );

  const logout = useCallback(async () => {
    await clearAuth();
    setUser(null);
  }, []);

  const value = useMemo<ProfessionalAuthContextValue>(() => {
    const permissions = Array.isArray(user?.permissions) ? user.permissions : [];

    return {
      ready,
      user,
      permissions,
      isAuthenticated: Boolean(user),
      defaultRoute: getDefaultAuthorizedRoute(permissions),
      login,
      selectTenant,
      logout,
      canAccess: (pathname: string) => canAccessPath(pathname, permissions)
    };
  }, [login, logout, ready, selectTenant, user]);

  return <ProfessionalAuthContext.Provider value={value}>{children}</ProfessionalAuthContext.Provider>;
}

export function useProfessionalAuth() {
  const context = useContext(ProfessionalAuthContext);
  if (!context) {
    throw new Error("useProfessionalAuth must be used within ProfessionalAuthProvider");
  }
  return context;
}
